/**
 * Heritage Forge — global theme scripts
 */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  /* Mobile menu */
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (menuToggle && mobileNav) {
    menuToggle.addEventListener('click', () => {
      const open = mobileNav.classList.toggle('is-open');
      menuToggle.setAttribute('aria-expanded', open);
      document.body.classList.toggle('menu-open', open);
    });
  }

  /* Cart drawer */
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartOverlay = document.querySelector('[data-cart-overlay]');
  const cartTriggers = document.querySelectorAll('[data-cart-open]');
  const cartCloseBtns = document.querySelectorAll('[data-cart-close]');

  function escapeHtml(str) {
    const el = document.createElement('div');
    el.textContent = str == null ? '' : String(str);
    return el.innerHTML;
  }

  function formatMoney(cents) {
    const format = window.theme?.moneyFormat || '${{amount}}';
    const amount = (Number(cents) / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(Number(cents) / 100));
    const amountWithComma = amount.replace('.', ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals);
  }

  function renderCartDrawer(cart) {
    if (!cartDrawer) return;

    const body = cartDrawer.querySelector('[data-cart-drawer-body]');
    const footer = cartDrawer.querySelector('[data-cart-drawer-footer]');
    if (!body || !footer) return;

    const emptyText = cartDrawer.dataset.emptyText || 'Your cart is empty';
    const continueUrl = cartDrawer.dataset.continueShoppingUrl || '/collections/all';
    const continueLabel = cartDrawer.dataset.continueShoppingLabel || 'Continue shopping';
    const subtotalLabel = cartDrawer.dataset.subtotalLabel || 'Subtotal';
    const viewCartUrl = cartDrawer.dataset.viewCartUrl || '/cart';
    const viewCartLabel = cartDrawer.dataset.viewCartLabel || 'View cart';
    const checkoutLabel = cartDrawer.dataset.checkoutLabel || 'Checkout';
    const removeLabel = cartDrawer.dataset.removeLabel || 'Remove';

    if (cart.item_count > 0) {
      const itemsHtml = cart.items.map((item) => {
        const title = item.product_title || item.title;
        const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="cart-drawer__item-variant">${escapeHtml(item.variant_title)}</p>`
          : '';
        const imageHtml = item.image
          ? `<a href="${escapeHtml(item.url)}" class="cart-drawer__item-image"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(title)}" loading="lazy" width="80" height="80"></a>`
          : '';

        return `<li class="cart-drawer__item">
          ${imageHtml}
          <div class="cart-drawer__item-details">
            <a href="${escapeHtml(item.url)}" class="cart-drawer__item-title">${escapeHtml(title)}</a>
            ${variantHtml}
            <p class="cart-drawer__item-price">${formatMoney(item.final_line_price)} &times; ${item.quantity}</p>
            <button type="button" class="cart-drawer__remove" data-cart-remove="${escapeHtml(item.key)}" aria-label="${escapeHtml(removeLabel)}">${escapeHtml(removeLabel)}</button>
          </div>
        </li>`;
      }).join('');

      body.innerHTML = `<ul class="cart-drawer__items">${itemsHtml}</ul>`;
      footer.innerHTML = `
        <div class="cart-drawer__subtotal">
          <span>${escapeHtml(subtotalLabel)}</span>
          <strong>${formatMoney(cart.total_price)}</strong>
        </div>
        <a href="${escapeHtml(viewCartUrl)}" class="btn btn--outline" style="width:100%;margin-bottom:0.5rem;">${escapeHtml(viewCartLabel)}</a>
        <form action="${escapeHtml(viewCartUrl)}" method="post">
          <button type="submit" name="checkout" class="btn btn--primary" style="width:100%;">${escapeHtml(checkoutLabel)}</button>
        </form>`;
      footer.hidden = false;
    } else {
      body.innerHTML = `
        <p class="cart-drawer__empty">${escapeHtml(emptyText)}</p>
        <a href="${escapeHtml(continueUrl)}" class="btn btn--primary">${escapeHtml(continueLabel)}</a>`;
      footer.innerHTML = '';
      footer.hidden = true;
    }
  }

  async function refreshCartDrawer() {
    const cartRes = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js');
    const cart = await cartRes.json();
    const countEl = document.querySelector('[data-cart-count]');
    if (countEl) countEl.textContent = cart.item_count;
    renderCartDrawer(cart);
    return cart;
  }

  function openCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartOverlay?.classList.add('is-open');
    document.body.classList.add('cart-open');
  }

  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartOverlay?.classList.remove('is-open');
    document.body.classList.remove('cart-open');
  }

  cartTriggers.forEach((el) => el.addEventListener('click', (e) => {
    e.preventDefault();
    openCart();
  }));
  cartCloseBtns.forEach((el) => el.addEventListener('click', closeCart));
  cartOverlay?.addEventListener('click', closeCart);

  async function removeCartLine(lineKey) {
    const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
    if (!res.ok) throw new Error('Remove failed');
    const cart = await refreshCartDrawer();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  }

  cartDrawer?.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-remove]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    try {
      await removeCartLine(btn.dataset.cartRemove);
    } catch (err) {
      console.error(err);
      btn.disabled = false;
    }
  });

  /* Search modal */
  const searchModal = document.querySelector('[data-search-modal]');
  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchTriggers = document.querySelectorAll('[data-search-open]');
  const searchCloseBtns = document.querySelectorAll('[data-search-close]');
  const searchInput = searchModal?.querySelector('.search-form__input');

  function openSearch() {
    if (!searchModal) return;
    searchModal.classList.add('is-open');
    searchOverlay?.classList.add('is-open');
    document.body.classList.add('search-open');
    searchInput?.focus();
  }

  function closeSearch() {
    if (!searchModal) return;
    searchModal.classList.remove('is-open');
    searchOverlay?.classList.remove('is-open');
    document.body.classList.remove('search-open');
  }

  searchTriggers.forEach((el) => el.addEventListener('click', (e) => {
    e.preventDefault();
    openSearch();
  }));
  searchCloseBtns.forEach((el) => el.addEventListener('click', closeSearch));
  searchOverlay?.addEventListener('click', closeSearch);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSearch();
  });

  document.addEventListener('cart:updated', async (e) => {
    if (e.detail?.cart && typeof e.detail.cart.item_count === 'number') {
      renderCartDrawer(e.detail.cart);
      const countEl = document.querySelector('[data-cart-count]');
      if (countEl) countEl.textContent = e.detail.cart.item_count;
      return;
    }
    await refreshCartDrawer();
  });

  /* Hero slider */
  const heroSlider = document.querySelector('[data-hero-slider]');
  if (heroSlider) {
    const slides = heroSlider.querySelectorAll('[data-hero-slide]');
    const dots = heroSlider.querySelectorAll('[data-hero-dot]');
    let current = 0;
    let timer;

    function goTo(index) {
      slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      current = index;
    }

    function next() {
      goTo((current + 1) % slides.length);
    }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        goTo(i);
        resetTimer();
      });
    });

    function resetTimer() {
      clearInterval(timer);
      if (slides.length > 1) timer = setInterval(next, 6000);
    }

    if (slides.length > 1) resetTimer();
  }

  /* Collection tabs (featured products) */
  document.querySelectorAll('[data-collection-tabs]').forEach((tabsRoot) => {
    const tabs = tabsRoot.querySelectorAll('[data-tab]');
    const panels = tabsRoot.querySelectorAll('[data-tab-panel]');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.dataset.tab;
        tabs.forEach((t) => t.classList.toggle('is-active', t === tab));
        panels.forEach((p) => p.classList.toggle('is-active', p.dataset.tabPanel === id));
      });
    });
  });

  /* AJAX add to cart / buy now */
  function initProductFormButtons() {
    document.querySelectorAll('[data-product-form] [type="submit"]').forEach((button) => {
      if (!button.dataset.defaultLabel) {
        button.dataset.defaultLabel = button.textContent.trim();
      }
    });
  }

  function resetProductFormButtons() {
    document.querySelectorAll('[data-product-form] [type="submit"]').forEach((button) => {
      const label = button.dataset.defaultLabel || button.textContent.trim();
      const fresh = button.cloneNode(true);
      fresh.disabled = false;
      fresh.removeAttribute('aria-busy');
      fresh.textContent = label;
      fresh.dataset.defaultLabel = label;
      button.replaceWith(fresh);
    });
  }

  function restoreProductPagesAfterNavigation() {
    resetProductFormButtons();
    initProductFormButtons();
  }

  initProductFormButtons();
  window.addEventListener('pageshow', restoreProductPagesAfterNavigation);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      restoreProductPagesAfterNavigation();
    }
  });

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();

    const btn = e.submitter || form.querySelector('[type="submit"]');
    const buyNow = btn?.matches('[data-buy-now]');
    const stayOnPage = form.hasAttribute('data-stay-on-page');
    const formButtons = form.querySelectorAll('[type="submit"]');
    const originalText = btn?.dataset.defaultLabel || btn?.textContent?.trim();

    formButtons.forEach((button) => {
      button.disabled = true;
      if (button === btn && !buyNow) {
        button.textContent = 'Adding…';
      } else if (button === btn) {
        button.setAttribute('aria-busy', 'true');
      }
    });

    function resetButton() {
      formButtons.forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        const label = button.dataset.defaultLabel;
        if (label) button.textContent = label;
      });
    }

    try {
      const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: form.querySelector('[name="id"]')?.value,
            quantity: parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10)
          }]
        })
      });

      if (!res.ok) throw new Error('Add failed');

      if (buyNow) {
        window.location.assign((window.Shopify?.routes?.root || '/') + 'checkout');
        return;
      }

      const cart = await refreshCartDrawer();

      if (stayOnPage) {
        if (btn) {
          btn.textContent = 'Added!';
          setTimeout(resetButton, 2000);
        } else {
          resetButton();
        }
      } else {
        openCart();
        resetButton();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      }
    } catch (err) {
      console.error(err);
      resetButton();
      form.submit();
    }
  });
})();
